import React from 'react';
import { ActivityIndicator, Pressable, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';

import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { useAiConsent } from '@/hooks/use-assistant';
import { showToast } from '@/store/toastStore';
import { getApiErrorMessage } from '@/utils/api-error';

const PRIMARY = '#FD5001';

const SENT = [
  'O que você escreve na conversa',
  'O que ele precisa da sua conta para responder: clientes e telefones, agenda, serviços, valores, cobranças e despesas',
];

/**
 * Pedido de permissão antes de qualquer dado ir à IA de terceiros (App Store
 * 5.1.2(i)): diz que vai a um parceiro de IA, o que vai e como desfazer. O nome
 * do parceiro (OpenRouter) fica na Política de Privacidade, no link do cartão. Sem o aceite, a API
 * recusa a conversa — este cartão é o único caminho até ela.
 */
export function AiConsentCard({
  onDecline,
  declineLabel = 'Agora não',
}: {
  /** "Agora não": quem chama decide para onde vai (lista de ramos, início). */
  onDecline: () => void;
  declineLabel?: string;
}) {
  const consent = useAiConsent();

  const accept = () =>
    consent.mutate(true, {
      onError: (err) =>
        showToast(getApiErrorMessage(err, 'Não foi possível salvar sua escolha.'), 'error'),
    });

  return (
    <ScrollView
      keyboardShouldPersistTaps="handled"
      contentContainerClassName="flex-grow justify-center px-5 py-8"
    >
      <View
        className="mx-auto w-full max-w-md gap-4 rounded-2xl border border-neutral-900/5 bg-card p-5"
        style={{ borderCurve: 'continuous' }}
      >
        <View className="h-10 w-10 items-center justify-center rounded-full bg-primary-50 dark:bg-primary">
          <Ionicons className="text-primary dark:text-white" name="sparkles-outline" size={20} />
        </View>

        <View className="gap-1.5">
          <ThemedText className="font-bold text-lg">Quer falar com nosso assistente?</ThemedText>
          <ThemedText className="text-sm leading-relaxed text-neutral-600">
            Ele entende o que você escreve e responde com base na sua conta. Para isso, usa a
            inteligência artificial de um parceiro, com quem compartilhamos:
          </ThemedText>
        </View>

        <View className="gap-2">
          {SENT.map((item) => (
            <View key={item} className="flex-row gap-2">
              <Ionicons
                className="text-neutral-500"
                name="arrow-forward"
                size={14}
                style={{ marginTop: 3 }}
              />
              <ThemedText className="flex-1 text-sm leading-relaxed text-neutral-600">
                {item}
              </ThemedText>
            </View>
          ))}
        </View>

        <ThemedText className="text-sm leading-relaxed text-neutral-600">
          Só compartilhamos com a sua permissão. Você pode desligar quando quiser no próprio
          assistente, e o resto do app continua funcionando normalmente.
        </ThemedText>

        <Pressable onPress={() => router.push('/privacidade')} hitSlop={8} className="self-start">
          <ThemedText className="text-sm text-primary underline">
            Política de Privacidade
          </ThemedText>
        </Pressable>

        <View className="gap-2">
          <PressableScale
            onPress={accept}
            disabled={consent.isPending}
            className="h-11 w-full items-center justify-center rounded-full bg-primary"
          >
            {consent.isPending ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <ThemedText className="font-bold text-sm text-white">
                Falar com o assistente
              </ThemedText>
            )}
          </PressableScale>
          <PressableScale
            onPress={onDecline}
            disabled={consent.isPending}
            className="h-11 w-full items-center justify-center rounded-full"
          >
            <ThemedText className="font-semibold text-sm text-neutral-600">
              {declineLabel}
            </ThemedText>
          </PressableScale>
        </View>
      </View>
    </ScrollView>
  );
}
